import { Skeleton } from "@/components/ui/skeleton";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useTasks } from "@/lib/hooks/useTasks";
import { CheckCircle2, CircleX, Clock, Loader2 } from "lucide-react";
import type { AgentTask } from "../../../../../schemas/codegen";

interface TaskListItemProps {
  task: AgentTask;
  taskNumber: number;
  totalTasks: number;
}

function TaskListItem({ task, taskNumber, totalTasks }: TaskListItemProps) {
  const getStatusIcon = () => {
    switch (task.status) {
      case "in_progress":
        return <Loader2 className="h-3 w-3 animate-spin text-blue-400" />;
      case "pending":
        return <Clock className="h-3 w-3 text-neutral-500" />;
      case "completed":
        return <CheckCircle2 className="h-3 w-3 text-green-400" />;
      case "cancelled":
        return <CircleX className="h-3 w-3 text-orange-400" />;
    }
  };

  return (
    <div className="grid grid-cols-[5ch_auto_1fr] gap-3 items-center">
      <span className="text-xs text-neutral-500 font-mono text-right">
        {taskNumber}/{totalTasks}
      </span>

      <div className="flex justify-center items-center">{getStatusIcon()}</div>

      <div className="min-w-0">
        <div className="text-sm text-neutral-300">{task.title}</div>
        {task.description && (
          <div className="text-xs text-neutral-500 mt-0.5">
            {task.description}
          </div>
        )}
      </div>
    </div>
  );
}

interface TaskListProps {
  tasks: AgentTask[];
}

function TaskList({ tasks }: TaskListProps) {
  // Sort tasks by sort order to maintain creation order
  const sortedTasks = [...tasks].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="grid gap-2 py-3">
      {sortedTasks.map((task, index) => (
        <TaskListItem
          key={task.id}
          task={task}
          taskNumber={index + 1}
          totalTasks={tasks.length}
        />
      ))}
    </div>
  );
}

export function TasksTab() {
  const activeProjectId = useActiveProjectId();
  const { data: tasks = [], isLoading } = useTasks(activeProjectId);

  return (
    <aside className="grid gap-3">
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full bg-neutral-800" />
          <Skeleton className="h-8 w-full bg-neutral-800" />
          <Skeleton className="h-8 w-full bg-neutral-800" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-4 text-center text-muted-foreground">
          <p className="text-sm">No tasks yet</p>
          <p className="text-xs mt-1">
            The agent will create tasks for complex workflows
          </p>
        </div>
      ) : (
        <TaskList tasks={tasks} />
      )}
    </aside>
  );
}
